/**
 * PWAUpdateBanner — visible when the SW has a waiting update.
 *
 * The renderer fires `hv:pwa-update-ready` on `window` when a new SW
 * enters the `installed` state. This banner listens for that event and
 * offers the user a one-click update.
 *
 * Design rules:
 *   - uncodixify-ui.md: no animate-bounce, transition-colors only.
 *   - accessibility: role="status" live region; dismiss button ≥ 44×44 px.
 *   - pwa-patterns.md: user controls the reload, no autoUpdate.
 */

import { useEffect, useRef, useState } from "react";

export interface PWAUpdateBannerStrings {
  banner: string;
  reload: string;
  reloadLater: string;
}

export function PWAUpdateBanner({ strings }: { strings: PWAUpdateBannerStrings }) {
  const [visible, setVisible] = useState(false);
  const waitingRef = useRef<ServiceWorker | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    function onUpdateReady(e: Event) {
      const detail = (e as CustomEvent<{ waiting: ServiceWorker }>).detail;
      waitingRef.current = detail?.waiting ?? null;
      setVisible(true);
    }

    window.addEventListener("hv:pwa-update-ready", onUpdateReady);
    return () => {
      window.removeEventListener("hv:pwa-update-ready", onUpdateReady);
    };
  }, []);

  function handleReload() {
    const sw = waitingRef.current;
    if (sw) {
      sw.postMessage({ type: "SKIP_WAITING" });
    }
    // Allow the new SW to activate before reloading.
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      window.location.reload();
    });
    // Fallback: force reload after 2 s if controllerchange doesn't fire.
    setTimeout(() => window.location.reload(), 2000);
  }

  function handleDismiss() {
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className={[
        "fixed bottom-0 inset-x-0 z-50 sm:bottom-4 sm:inset-x-auto sm:end-4",
        "flex items-center justify-between gap-3 px-4 py-3",
        "bg-[var(--color-bg-surface-elevated)] border border-[var(--color-border-default)]",
        "rounded-t-xl sm:rounded-xl shadow-sm",
        "sm:max-w-sm",
      ].join(" ")}
    >
      <p className="text-sm text-[var(--color-fg-default)]">{strings.banner}</p>
      <div className="flex shrink-0 items-center gap-2">
        <button
          type="button"
          onClick={handleReload}
          className="rounded-md bg-[var(--color-accent-info)] px-3 py-2 text-xs font-semibold text-white transition-colors duration-150 hover:opacity-90 min-h-[44px]"
        >
          {strings.reload}
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label={strings.reloadLater}
          className="flex items-center justify-center rounded-md p-2 min-h-[44px] min-w-[44px] text-[var(--color-fg-muted)] transition-colors duration-150 hover:bg-[var(--color-bg-surface)]"
        >
          <svg
            aria-hidden
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            className="h-4 w-4"
          >
            <path d="M2 2l12 12M14 2L2 14" />
          </svg>
        </button>
      </div>
    </div>
  );
}
